import React, { useState, useEffect } from "react";
import { Text } from "@mantine/core";
import TagBadge, { tagBadgeColor, tagName } from "./TagBadge";

const manualTagOrder = [
  "like",
  "veryGood",
  "good",
  "finished",
  "bad",
  "inProgress",
  "todo",
];
const manualTagOrderIndex = manualTagOrder.reduce((acc, name, index) => {
  acc[name] = index;
  return acc;
}, {});

const SelectableTags = ({ relevantTags, alreadySelectedTags = [], onSelectionChange, language }) => {
  const [selectedTags, setSelectedTags] = useState([]);
  const [showAllTags, setShowAllTags] = useState(false);

  useEffect(() => {
    setSelectedTags(alreadySelectedTags);
  }, [alreadySelectedTags]);

  const toggleTagSelection = (selectedTag) => {
    let updatedSelection;
    if (selectedTags.find((t) => t.tag.name === selectedTag.tag.name)) {
      updatedSelection = selectedTags.filter((t) => t.tag.name !== selectedTag.tag.name);
    } else {
      updatedSelection = [...selectedTags, selectedTag];
    }
    setSelectedTags(updatedSelection);
    onSelectionChange(updatedSelection);
  };

  const alwaysVisibleTypes = new Set([2, 3, 4]);
  const selectedTagIds = new Set(selectedTags.map((tagObject) => tagObject.tag.id));
  const filteredTags = showAllTags
    ? relevantTags
    : relevantTags.filter(
        (tagObject) =>
          alwaysVisibleTypes.has(tagObject.tag.type) || selectedTagIds.has(tagObject.tag.id)
      );

  const hasAdditionalTags = relevantTags.some(
    (tagObject) => !alwaysVisibleTypes.has(tagObject.tag.type)
  );
  const orderedTags = [...filteredTags].sort((a, b) => {
    if (a.tag.type !== 3 || b.tag.type !== 3) {
      return 0;
    }
    const aIndex = manualTagOrderIndex[a.tag.name];
    const bIndex = manualTagOrderIndex[b.tag.name];
    if (aIndex === undefined || bIndex === undefined) {
      return 0;
    }
    return aIndex - bIndex;
  });

  return (
    <div>
      <div className={`flex flex-wrap items-center gap-2`}>
        {orderedTags.map((tagObject, index) => {
          const { name } = tagObject.tag;
          const { count } = tagObject;
          const { type } = tagObject.tag;
          const isSelected = selectedTags.find((t) => t.tag.name === name);

          return (
            <TagBadge
              key={index}
              component="button"
              type="button"
              className={`cursor-pointer ${isSelected ? "ring-2 ring-white/90" : ""}`}
              radius="md"
              size="md"
              color={tagBadgeColor(name, type)}
              variant="light"
              onClick={() => toggleTagSelection(tagObject)}
            >
              {tagObject.tag.imgUrl !== "" ? (
                <div className="flex items-center text-center max-h-6 gap-1">
                  <span>#</span>
                  <img className="w-5 max-w-none h-5 rounded-full" src={tagObject.tag.imgUrl} />
                  <span>{tagName(name, language)}</span>
                  <Text span size="xs" c="white" style={{ opacity: 0.85 }}>
                    ({count})
                  </Text>
                </div>
              ) : (
                <>
                  {tagName(name, language)}&nbsp;
                  <Text span size="xs" c="white" style={{ opacity: 0.85 }}>
                    ({count})
                  </Text>
                </>
              )}
            </TagBadge>
          );
        })}
        {!showAllTags && hasAdditionalTags && (
          <TagBadge
            type="button"
            component="button"
            radius="md"
            size="md"
            variant="light"
            color="teal"
            className="cursor-pointer"
            onClick={() => setShowAllTags(true)}
            title="Show all tags"
          >
            {language === "ru" ? "Показать все" : "Show all"}
          </TagBadge>
        )}
        {showAllTags && (
          <TagBadge
            type="button"
            component="button"
            radius="md"
            size="md"
            variant="light"
            color="teal"
            className="cursor-pointer"
            onClick={() => setShowAllTags(false)}
            title="Hide tags"
          >
            {language === "ru" ? "Скрыть" : "Hide"}
          </TagBadge>
        )}
      </div>
    </div>
  );
};

export default SelectableTags;
